import { Component, Input, OnInit, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { faExchangeAlt, faInfoCircle, faChevronDown, faSpinner, faCheck } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { BalanceAccountService } from 'src/app/share/services/balance-account/balance-account.service';

@Component({
  selector: 'app-new-balance-transfer-modal',
  templateUrl: './new-balance-transfer-modal.component.html',
  styleUrls: ['./new-balance-transfer-modal.component.scss'],
  standalone: false
})
export class NewBalanceTransferModalComponent implements OnInit {
  protected readonly faExchangeAlt = faExchangeAlt;
  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faSpinner = faSpinner;
  protected readonly faCheck = faCheck;

  @Input() defaultSourceAccountId: string = '';
  @Input() defaultDestinationAccountId: string = '';

  transferForm!: FormGroup;
  balanceAccounts: any[] = [];
  destinationBalanceAccounts: any[] = [];
  accountsLoader: boolean = false;

  saveLoader: boolean = false;
  errorMessage: string = '';

  maxDate!: NgbDateStruct;
  selectedNgbDate!: NgbDateStruct;

  @ViewChild('dateContainer') dateContainer!: ElementRef;

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly fb: FormBuilder,
    private readonly balanceAccountService: BalanceAccountService
  ) {}

  ngOnInit(): void {
    const today = new Date();
    this.maxDate = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate()
    };

    this.initForm();
    this.loadBalanceAccounts();
  }

  initForm(): void {
    const notZeroValidator = (control: any) => {
      const val = control.value;
      if (val !== null && val !== undefined && val !== '' && Number(val) <= 0) {
        return { invalidAmount: true };
      }
      return null;
    };

    const sourceId = this.defaultSourceAccountId || null;

    this.transferForm = this.fb.group({
      sourceAccountId: [{ value: sourceId, disabled: !!sourceId }, Validators.required],
      destinationAccountId: [this.defaultDestinationAccountId || null, Validators.required],
      amount: [null, [Validators.required, notZeroValidator]],
      transferDate: [dayjs().format('YYYY-MM-DD'), Validators.required],
      notes: ['']
    });

    this.updateNgbDate(this.transferForm.get('transferDate')?.value);
  }

  filterDestinationAccounts(): void {
    const sourceId = this.defaultSourceAccountId || (this.transferForm ? this.transferForm.getRawValue().sourceAccountId : null);
    if (!sourceId) {
      this.destinationBalanceAccounts = [...this.balanceAccounts];
    } else {
      this.destinationBalanceAccounts = this.balanceAccounts.filter((acc: any) => acc._id !== sourceId);
    }
  }

  loadBalanceAccounts(): void {
    this.accountsLoader = true;
    this.balanceAccountService.getBalanceAccounts({ status: 'Active' }).subscribe({
      next: (res: any) => {
        this.accountsLoader = false;
        this.balanceAccounts = res?.data || [];
        this.filterDestinationAccounts();
      },
      error: () => {
        this.accountsLoader = false;
      }
    });
  }

  onDateSelect(date: NgbDateStruct): void {
    if (date) {
      const dateStr = `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
      this.transferForm.get('transferDate')?.setValue(dateStr);
      this.selectedNgbDate = date;
    }
  }

  updateNgbDate(dateStr: string): void {
    if (dateStr && dateStr.split('-').length === 3) {
      const parts = dateStr.split('-');
      this.selectedNgbDate = {
        year: Number.parseInt(parts[0], 10),
        month: Number.parseInt(parts[1], 10),
        day: Number.parseInt(parts[2], 10)
      };
    } else {
      const today = new Date();
      this.selectedNgbDate = {
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: today.getDate()
      };
    }
  }

  get transferDateFormatted(): string {
    const val = this.transferForm.get('transferDate')?.value;
    return val ? dayjs(val).format('DD MMM YYYY') : 'Select Date';
  }

  onNumberKeyDown(event: KeyboardEvent): void {
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onNumberWheel(event: WheelEvent): void {
    event.preventDefault();
  }

  saveTransfer(): void {
    if (this.transferForm.invalid) {
      this.transferForm.markAllAsTouched();
      return;
    }

    if (this.saveLoader) return;

    const formValues = this.transferForm.getRawValue();

    if (formValues.sourceAccountId && formValues.destinationAccountId && formValues.sourceAccountId === formValues.destinationAccountId) {
      this.errorMessage = 'Source and destination accounts cannot be the same';
      return;
    }

    this.saveLoader = true;
    this.errorMessage = '';

    const payload = {
      sourceAccountId: formValues.sourceAccountId,
      destinationAccountId: formValues.destinationAccountId,
      amount: Number(formValues.amount),
      transferDate: formValues.transferDate,
      notes: formValues.notes ? formValues.notes.trim() : ''
    };

    this.balanceAccountService.createBalanceTransfer(payload).subscribe({
      next: (res: any) => {
        this.saveLoader = false;
        this.activeModal.close('SAVED');
      },
      error: (err: any) => {
        this.saveLoader = false;
        this.errorMessage = err?.error?.error?.message || err?.error?.message || 'Failed to create balance transfer';
      }
    });
  }
}
